import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';

import { AccessPolicy, PolicyPostRequest, Statement } from '@mitel/cloudlink-sdk/admin';
import { AdminService } from './../../../core/services/admin.service';
import { AccountsService } from './../../../core/services/accounts.service';
import { ProgressIndicatorPopupComponent } from './../../../shared/components/progress-indicator-popup/progress-indicator-popup.component';
import { FeatureFlagOperation, FeatureFlagItem, featureFlagPolicyConst } from './../../../core/interfaces/feature-flags';
import { Util } from './../../../core/utils/Util';
import { environment } from './../../../../environments/environment';

export interface UpdateAccountFail {
  accountId: string;
  error: { body: any, statusCode: number };
}

/*
  Feature flag users will be defining their role based on the user policyId
  policyId : "feature-flag-admin-${{cloud}}-${{environment}}" -> ADMINISTRATOR
  policyId : "feature-flag-coord-${{cloud}}-${{environment}}" -> COORDINATOR
*/
export enum FeatureFlagRoles {
  ADMINISTRATOR = 'administrator',
  COORDINATOR = 'coordinator',
  UNKNOWN = 'unknown' //unlikely
}

export enum FeatureFlagActions {
  VIEW = 'view',
  EDIT = 'edit',
  ENABLE = 'enable',
  RELEASE = 'release',
  DELETE = 'delete'
}

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagPolicyService {
  private _featureFlagRole = FeatureFlagRoles.UNKNOWN;

  private featureFlagRoles = [
    { 
      name: FeatureFlagRoles.ADMINISTRATOR, 
      policyIdPrefix: 'feature-flag-admin',
      actions: [FeatureFlagActions.VIEW, FeatureFlagActions.EDIT, FeatureFlagActions.ENABLE, FeatureFlagActions.RELEASE, FeatureFlagActions.DELETE]
    },
    { 
      name: FeatureFlagRoles.COORDINATOR, 
      policyIdPrefix: 'feature-flag-coord',
      actions: [FeatureFlagActions.VIEW, FeatureFlagActions.ENABLE]
    },
    { 
      name: FeatureFlagRoles.UNKNOWN, 
      policyIdPrefix: '', 
      actions: [FeatureFlagActions.VIEW]
    }
  ];

  constructor(private adminService: AdminService,
    private accountService: AccountsService,
    private modalService: NgbModal,
    private translateService: TranslateService) { }


  get featureFlagRole() {
    return this._featureFlagRole;
  }

  async updateFeatureFlagAccountAssignation(featureFlag: FeatureFlagItem, operation: FeatureFlagOperation, accountIds: string[], accessPolicies: AccessPolicy[]): Promise<string[]> {
    if (featureFlag && accountIds.length) {
      const failedAccounts = await this.updateFeatureFlagForAccounts(featureFlag, operation, accountIds, accessPolicies);
      accountIds = accountIds.filter(accountId => !failedAccounts.some(failedAccount => failedAccount.accountId === accountId));
      return accountIds;
    }
    return [];
  }

  async updateFeatureFlagForAccounts(featureFlag: FeatureFlagItem, operation: FeatureFlagOperation, accountIds: string[], accessPolicies: AccessPolicy[]): Promise<UpdateAccountFail[]> {
    const space = '<br/>&nbsp;&nbsp;';
    let progressUpdate = new Subject();
    let progressData = {
      total: accountIds.length,
      done: 0,
      errorMessage: undefined,
      title: operation ? `feature_flags.progress_indicator.${operation}_feature_flag_title` : `feature_flags.progress_indicator.${operation}_feature_flag_title`,
      actionString: operation ? `feature_flags.progress_indicator.${operation}_feature_flag_action` : `feature_flags.progress_indicator.${operation}_feature_flag_action`,
      successMessage: undefined,
      updateSubject: progressUpdate
    }
    let modal = this.modalService.open(ProgressIndicatorPopupComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modal.componentInstance.progressData = progressData;

    let failedAccounts: UpdateAccountFail[] = [];
    let featureFlagResource = 'https://mitel.io/auth/feature-flag/features/' + featureFlag.id;

    for (var i = 0; i < accountIds.length; i++) {
      const accountId = accountIds[i];
      // to prevent the client from hammering the system
      await Util.timeout(10);

      try {
        let featureFlagPolicy: AccessPolicy = accessPolicies[i] ? JSON.parse(JSON.stringify(accessPolicies[i])) : undefined;
        switch (operation) {
          case FeatureFlagOperation.ENABLE:
            let featureFlagStatement = {
              statementId: featureFlagPolicyConst.statementId,
              action: ['*'],
              resource: [featureFlagResource],
              effect: 'Allow' as const
            };
            if (featureFlagPolicy) {
              let statementUpdated = this.addFeatureFlagResourceForPolicy(featureFlagResource, featureFlagPolicy);
              if (!statementUpdated) {
                await this.adminService.updateUserPolicyStatement(accountId, featureFlagPolicy.policyId, featureFlagStatement);
                featureFlagPolicy.statements.push(featureFlagStatement);
              } else {
                await this.adminService.updateUserPolicyStatement(accountId, featureFlagPolicy.policyId, statementUpdated);
              }
              accessPolicies[i] = featureFlagPolicy;
            } else {
              // account doesn't have feature flags enabled, add policy
              const policy: PolicyPostRequest = {
                policyId: featureFlagPolicyConst.policyId,
                name: featureFlagPolicyConst.policyName,
                statements: [featureFlagStatement],
                type: 'feature-flag'
              };
              accessPolicies[i] = await this.adminService.createUserPolicy(policy, accountId);
            }
            break;
          case FeatureFlagOperation.DISABLE:
            if (featureFlagPolicy) {
              let statementUpdated = this.removeFeatureFlagResourceFromPolicy(featureFlagResource, featureFlagPolicy);
              if (statementUpdated) {
                if (featureFlagPolicy.statements.length == 0) {
                  await this.adminService.deletePolicyById(accountId, featureFlagPolicy.policyId);
                  accessPolicies[i] = undefined;
                } else {
                  await this.adminService.updateUserPolicyStatement(accountId, featureFlagPolicy.policyId, statementUpdated);
                  accessPolicies[i] = featureFlagPolicy;
                }
              } else {
                throw { body: 'Policy statement not found for feature flag' };
              }
            } else {
              throw { body: 'Policy not found for feature flag' };
            }
            break;
          default:
            console.warn(`Operation not defined for feature flag: ${operation}`);
            throw { body: 'Operation not defined for feature flag' };
        }
      } catch (error) {
        console.error(`Error has occurred while trying to ${operation} for account: ${accountId}`, error);
        let errorInfo: UpdateAccountFail = {
          accountId: accountId,
          error: { body: error.body, statusCode: error.statusCode }
        }
        failedAccounts.push(errorInfo);

        // 404 -> "AccountNotFound" or "PolicyIdNotFound"
        if (error.statusCode && error.statusCode == 404) {
          continue;
        }

        // to create a better message, we need the account name
        let acct = await this.accountService.getAccountById(accountId);
        if (!progressData.errorMessage) {
          let message = `feature_flags.progress_indicator.${operation}_feature_flag_failed`;
          progressData.errorMessage = this.translateService.instant(message) + ':' + space +
            (acct ? acct.name : accountId);
        }
        else { // just append the name after a newline
          progressData.errorMessage = progressData.errorMessage + space + (acct ? acct.name : accountId);
        }
      }

      progressData.done++;
      progressUpdate.next(progressData);
    };

    progressData.successMessage = this.getFinalMessage(progressData.done - failedAccounts.length, operation);
    progressUpdate.next(progressData);

    return modal.result.then(result => {
      modal = null;
      progressUpdate = null;
      console.log('Here is the failed list', failedAccounts);
      return failedAccounts;
    });
  }

  addFeatureFlagResourceForPolicy(featureFlagResource: string, policyFeature?: AccessPolicy): Statement {
    let statementUpdated = undefined;
    if (featureFlagResource && policyFeature?.policyId === featureFlagPolicyConst.policyId) {
      for (let i = 0; i < policyFeature.statements?.length; i++) {
        let statement = policyFeature.statements[i];
        if (statement.statementId === featureFlagPolicyConst.statementId) {
          statement.resource.push(featureFlagResource);
          statementUpdated = statement;
        }
      }
    }
    return statementUpdated;
  }

  removeFeatureFlagResourceFromPolicy(featureFlagResource: string, policyFeature?: AccessPolicy): Statement {
    let statementUpdated = undefined;
    if (featureFlagResource && policyFeature?.policyId === featureFlagPolicyConst.policyId) {
      let deleteStatementIndex = -1;
      for (let i = 0; i < policyFeature.statements?.length; i++) {
        let statement = policyFeature.statements[i];
        if (statement?.statementId === featureFlagPolicyConst.statementId) {
          let index = statement.resource?.indexOf(featureFlagResource);
          if (index >= 0) {
            statement.resource.splice(index, 1);
            statementUpdated = statement;
            if (statement.resource.length == 0) {
              deleteStatementIndex = i;
            }
          }
        }
      }
      if (deleteStatementIndex >= 0) {
        policyFeature.statements.splice(deleteStatementIndex, 1);
      }
    }
    return statementUpdated;
  }

  getFinalMessage(numAccounts: number, operation: FeatureFlagOperation): string {
    if (numAccounts > 0)
      return this.translateService.instant(`feature_flags.progress_indicator.${operation}_feature_flag_done`, { numAccounts: numAccounts });
    else
      return this.translateService.instant(`feature_flags.progress_indicator.${operation}_feature_flag_none_done`);
  }

  // releases the feature flag by adding it as a resource to the system-policy
  // accountId: *
  // statementId: allow-feature-flags-account
  // each feature flag released will be added as a resource, same value used in the account policy by 'type: feature-flag'
  // 2017-09-01/accounts/*/policies/system-policy.${environment}.${region}
  async releaseFeatureFlag(featureFlag: FeatureFlagItem, region: string) {
    if (featureFlag?.id && region) {
      let systemPolicy = await this.adminService.getPolicy('*', `${environment.systemPolicyId}.${region}`);
      if (systemPolicy) {
        let featureFlagResource = 'https://mitel.io/auth/feature-flag/features/' + featureFlag.id;
        let featureFlagStatement = {
          statementId: featureFlagPolicyConst.statementId,
          action: ['*'],
          resource: [featureFlagResource],
          effect: 'Allow' as const
        };
        for (let i = 0; i < systemPolicy.statements?.length; i++) {
          let statement = systemPolicy.statements[i];
          if (statement?.statementId === featureFlagPolicyConst.statementId) {
            statement.resource.push(featureFlagResource);
            featureFlagStatement = statement as any;
          }
        }
        await this.adminService.updateUserPolicyStatement('*', systemPolicy.policyId, featureFlagStatement);
      } else {
        throw 'Error occurred while releasing the feature flag, system policy not found';
      }
    } else {
      throw 'Error occurred while releasing the feature flag, missing parameters';
    }
  }
  
  // removes a released feature flag by deleting its entry resource in the system-policy
  // accountId: *
  // statementId: allow-feature-flags-account
  // each feature flag released will be added as a resource, same value used in the account policy by 'type: feature-flag'
  // 2017-09-01/accounts/*/policies/system-policy.${environment}.${region}
  async removeReleasedFeatureFlag(featureFlag: FeatureFlagItem, region: string) {
    if (featureFlag?.id && region) {
      let systemPolicy = await this.adminService.getPolicy('*', `${environment.systemPolicyId}.${region}`);
      if (systemPolicy) {
        let featureFlagResource = 'https://mitel.io/auth/feature-flag/features/' + featureFlag.id;
        let featureFlagStatement = undefined;
        for (let i = 0; i < systemPolicy.statements?.length; i++) {
          let statement = systemPolicy.statements[i];
          if (statement?.statementId === featureFlagPolicyConst.statementId) {
            let index = statement.resource?.indexOf(featureFlagResource);
            if (index >= 0) {
              statement.resource.splice(index, 1);
            }
            featureFlagStatement = statement as any;
          }
        }
        // if featureFlagStatement is undefined means no feature flag statement was found in system policy
        // not need to update the policy then
        if (featureFlagStatement) {
          if (featureFlagStatement.resource.length == 0) {
            await this.adminService.removeStatementFromPolicy('*', systemPolicy.policyId, featureFlagPolicyConst.statementId);
          } else {
            await this.adminService.updateUserPolicyStatement('*', systemPolicy.policyId, featureFlagStatement);
          }
        } else {
          console.warn('Released feature flag not found in system policy');
        }
      } else {
        throw 'Error occurred while deleting the released feature flag, system policy not found';
      }
    } else {
      throw 'Error occurred while deleting the released feature flag, missing parameters';
    }
  }

  setupFeatureFlagPermissions(claims: any) {
    this._featureFlagRole = FeatureFlagRoles.UNKNOWN;
    if (claims?.policyId) {
      this.featureFlagRoles.forEach(role => {
        //only one match
        if (role.policyIdPrefix && claims.policyId.indexOf(role.policyIdPrefix) != -1) {
          this._featureFlagRole = role.name
        }
      });
    }
  }

  isFeatureFlagRoleAllowedTo(action: FeatureFlagActions): boolean {
    let actions = this.featureFlagRoles.find(role => role.name === this._featureFlagRole)?.actions ?? [];

    return Boolean(actions?.find(ac => ac == action));
  }
}
